import mori from 'mori';
import d3 from 'd3';
import Datamap from 'datamaps';
import generateLegend from '../util/d3.legend/d3.legend';
import { appDefault } from './config';
// Country Codes for World Map
import { countries, countryCodes } from './countryMapCodes';

export const mapColors = {
  'defaultColor': '#b6b6b6',
  '1': ['#014f87'],
  '2': ['#99ddff', '#014f87'],
  '3': ['#99ddff', '#014f87', '#13283c'],
  '4': ['#99ddff', '#2a91c2', '#014f87', '#13283c'],
  '5': ['#d6f1ff', '#99ddff', '#2a91c2', '#014f87', '#13283c'],
  '6': ['#d6f1ff', '#99ddff', '#2a91c2', '#0b69c1', '#014f87', '#13283c'],
  'full': [
    '#d6f1ff',
    '#99ddff',
    '#47c2ff',
    '#2a91c2',
    '#0d7ee7',
    '#0b69c1',
    '#014f87',
    '#13283c',
  ],
};

// Datamaps default configs
export function mapDefault(scope) {
  return mori.toClj({
    geographyConfig: {
      highlightBorderColor: '#FFFFFF',
      popupTemplate: (geography, data) => {
        const geoValue = (data || {}).formatted || 'N/A';
        const geoName = (scope === 'usa') ?
          geography.properties.name :
          countries[countryCodes[geography.id]];

        if (data.mapBaseColumn && data.value) {
          const nonGeoMapData = data.value[geography.id];

          const items = `${Object.keys(nonGeoMapData).map(
            (key) => `<strong>${key}</strong><br/>${nonGeoMapData[key].map(
              (item) =>
                `<em>${Object.keys(item)[0]}:</em>
                  ${item[Object.keys(item)[0]]}`
            ).join('<br/>')
            }`
          )}`;

          // @TODO - find better markup for these tooltips
          return `<div class="datamaps-hover wide">
          <span><span><p class="mapBaseColumn"><span>
            ${items}
          </span></p></span></span>
          </div>`;
        }

        return (`
  <div class="datamaps-hover">
  <span><span>
  <p><span>${geoName}: ${geoValue}</span></p>
  </span></span>
  </div>`);
      },
      borderWidth: 1,
      highlightBorderWidth: 2,
    },
    options: {
      labels: false,
      legend: false,
      width: 753,
      height: 452,
      legendHeight: 100,
    },
    fills: {},
  });
}

// Map the geography codes so we can assign them properly
export function getGeoCodeMap(geoContext) {
  return Datamap.prototype[geoContext + 'Topo'].objects[geoContext].geometries
    .reduce((acc, curr) => {
      acc[curr.properties.name] = curr.id;
      return acc;
    }, {});
}

/**
 * Return the proper projection for the map
 */
export function setMapProjection(geoContext, mapConfig) {
  if (geoContext === 'world') {
    return function setProjection(element) {
      const projection = d3.geo.vanDerGrinten3()
            .center([0, 10])
            .scale(129)
            .translate([
              (element.offsetWidth / 2) - 10,
              element.offsetHeight / 2,
            ]);
      const path = d3.geo.path()
            .projection(projection);
      return { path, projection };
    };
  }
  return function setProjection() {
    const projection = d3.geo.albersUsa()
          .scale(mapConfig.options.width * 1.3)
          .translate([
            mapConfig.options.width / 2,
            mapConfig.options.height / 2,
          ]);
    const path = d3.geo.path()
          .projection(projection);
    return { path, projection };
  };
}

/**
 * Sets up the custom legend for KFF
 */

export function mapCustomLegend() {
  d3.select(this.options.element)
    .select('.datamaps-legend')
    .remove();

  if (!this.options.fills) {
    return;
  }

  const legendItems = Object.keys(this.options.fills).reduce((out, fillKey) => {
    let label = '';
    if (fillKey === 'defaultFill') {
      if (!this.options.labels[fillKey] ||
        this.options.labels[fillKey] === 'N/A') {
        return out;
      }
      label = this.options.labels[fillKey];
    } else {
      if (this.options.labels && this.options.labels[parseInt(fillKey, 10)]) {
        label = this.options.labels[parseInt(fillKey, 10)];
      } else {
        label = fillKey;
      }
    }
    out.push({
      'name': label,
      'color': this.options.fills[fillKey],
    });

    return out;
  }, []);

  const hasNoDataItems = !!Object.keys(this.options.data)
    .filter(
      (key) =>
        appDefault.dataNotAvailable.indexOf(this.options.data[key].raw) >= 0
    ).length;
  if (hasNoDataItems && this.options.labels.hasOwnProperty('defaultFill')) {
    legendItems.push({
      'name': 'N/A',
      'color': this.options.fills.defaultFill,
    });
  }

  const margin = { top: 5, right: 0, bottom: 5, left: 0 };
  const width = 753;
  const top = 460;
  const left = 10;
  const height = (legendItems.length % 4 === 0) ?
    legendItems.length / 4 * 50 :
    (Math.floor(legendItems.length / 4) + 1) * 50;

  generateLegend(
    this.options.element.children[0],
    legendItems,
    {
      margin,
      width,
      height,
      top,
      left,
      wrapper: 'g',
      isMap: true,
    }
  );

  const legendHeight = d3.select(this.options.element.children[0])
    .select('.datamaps-legend').node().getBBox().height + margin.top;

  // Update Height
  d3.select(this.options.element.children[0])
    .attr('height', top + Math.max(height, legendHeight));

  // Remove bottom padding
  const padding = (height >= 100) ? 20 : 100 - height;
  this.options.element.style.paddingBottom = `${padding}px`;
}

/**
 * Sets up custom state labels for outside boxes
 */
export function mapCustomLabels(layer) {
  const _this = this;
  const labelStartCoodinates = _this.projection([-67.707617, 42.722131]);

  d3.selectAll('.datamaps-subunit')
    .attr('data-foo', function(d) {
      const center = _this.path.centroid(d);
      const smallStateIndex = [
        'VT',
        'NH',
        'MA',
        'RI',
        'CT',
        'NJ',
        'DE',
        'MD',
        'DC',
        'HI',
      ].indexOf(d.id);
      let xOffset = 7.5;
      let yOffset = 5;
      let x;
      let y;

      if (['FL', 'KY', 'MI'].indexOf(d.id) > -1) {
        xOffset = -2.5;
      }
      if (d.id === 'NY') {
        xOffset = -1;
      }
      if (d.id === 'MI') {
        yOffset = 18;
      }
      if (d.id === 'LA') {
        xOffset = 13;
      }
      if (d.id === 'HI') {
        xOffset = 30;
      }

      x = center[0] - xOffset;
      y = center[1] + yOffset;

      if (smallStateIndex > -1) {
        if (d.id !== 'HI') {
          const yStart = labelStartCoodinates[1];
          x = labelStartCoodinates[0];
          y = yStart + (smallStateIndex * (2 + (_this.options.fontSize || 12)));
          layer.append('line')
            .attr('x1', x - 3)
            .attr('y1', y - 5)
            .attr('x2', center[0])
            .attr('y2', center[1])
            .style('stroke', _this.options.labelColor || '#DDD')
            .style('stroke-width', _this.options.lineWidth || 1);
        }

        layer.append('rect')
          .attr('width', '24px')
          .attr('height', '16px')
          .attr(
            'fill',
            _this.options.fills[(_this.options.data[d.id] || {}).fillKey] ||
            _this.options.fills.defaultFill
          )
          .attr('x', x - 4)
          .attr('y', y - 12)
          .attr('data-info', JSON.stringify(_this.options.data[d.id]))
          .on('mouseover', function() {
            const $this = d3.select(this).attr(
              'data-info',
              JSON.stringify(_this.options.data[d.id])
            );

            _this.options.popupTemplate = _this
              .options.geographyConfig.popupTemplate;
            _this.updatePopup($this, d, _this.options, _this.svg);

            const $state = _this.svg.select('.' + d.id);
            const prevAttr = {
              'fill': $state.style('fill'),
              'stroke': $state.style('stroke'),
              'stroke-width': $state.style('stroke-width'),
              'fill-opacity': $state.style('fill-opacity'),
            };

            $this
              .style('fill', _this.options.bubblesConfig.highlightFillColor)
              .style(
                'fill-opacity',
                _this.options.bubblesConfig.highlightFillOpacity
              );

            $state
              .style('fill', _this.options.bubblesConfig.highlightFillColor)
              .style(
                'stroke',
                _this.options.bubblesConfig.highlightBorderColor
              )
              .style(
                'stroke-width',
                _this.options.bubblesConfig.highlightBorderWidth
              )
              .style(
                'fill-opacity',
                _this.options.bubblesConfig.highlightFillOpacity
              )
              .attr('data-previousAttributes', JSON.stringify(prevAttr));
          })
          .on('mouseout', function() {
            const $this = d3.select(this);
            const $state = _this.svg.select('.' + d.id);
            const prevAttr = JSON.parse($state.attr('data-previousAttributes'));
            for (const attr in prevAttr) {
              if (attr) {
                $state.style(attr, prevAttr[attr]);
                if (attr === 'fill' || attr === 'fill-opacity') {
                  $this.style(attr, prevAttr[attr]);
                }
              }
            }
            d3.selectAll('.datamaps-hoverover').style('display', 'none');
          });
      }

      layer.append('text')
        .attr('x', x)
        .attr('y', y)
        .style('font-size', _this.options.fontSize || 12)
        .style('font-weight', 'bold')
        .style(
          'font-family',
          _this.options.fontFamily || 'Arial, Helvetica, sans-serif'
        )
        .style(
          'fill',
          ['#99ddff', '#d6f1ff'].indexOf(
            _this.options.fills[(_this.options.data[d.id] || {}).fillKey]
          ) !== -1 ?
          '#13283c' :
          _this.options.labelColor ||
          '#FFF'
        )
        .style('pointer-events', 'none')
        .text(d.id);
    });
}



// WEBPACK FOOTER //
// ../datacenter/app/js/config/maps.js